/* ─── Cartes de Ligue (système inspiré de TFT) ──────────────────────────────
   16 cartes. Chaque carte appartient à 1 à 3 FAMILLES selon sa rareté :
     • Commun (gris)      → 1 famille
     • Rare (bleu)        → 1 famille
     • Épique (violet)    → 2 familles
     • Légendaire (or)    → 3 familles
   Plus une carte a de familles, plus elle aide à débloquer des combos.

   Chaque joueur envoie UN atout sur le terrain d'équipe partagé. Les combos
   se forment entre les cartes de même famille envoyées par toute l'équipe.

   Raretés pondérées (Tiers) pour la RNG du shop :
     • TIER 1 (Commun)     : 60 %
     • TIER 2 (Rare)       : 28 %
     • TIER 3 (Épique)     : 9 %
     • TIER 4 (Légendaire) : 3 %                                                */

export type CardFamily = "tribune" | "staff" | "environnement" | "vip";
export type CardTier = 1 | 2 | 3 | 4;

export interface CardDef {
  id: string;
  name: string;
  families: CardFamily[]; // 1 à 3 selon la rareté ; [0] = famille principale
  tier: CardTier;
  icon: string;
  effect: string;
}

/* ─── Familles (méta d'affichage) ──────────────────────────────────────────── */
export interface FamilyMeta {
  key: CardFamily;
  label: string;
  synergy: string;
  color: string;
  icon: string;
  comboName: string;
  combo2: string; // bonus à 2 cartes de la famille sur le terrain
  combo4: string; // bonus à 4 cartes de la famille sur le terrain
}

export const FAMILIES: Record<CardFamily, FamilyMeta> = {
  tribune: {
    key: "tribune",
    label: "The Stands",
    synergy: "End-game bonus and pressure",
    color: "#FF6B3D",
    icon: "📣",
    comboName: "Cauldron",
    combo2:
      "If the team is losing at half-time, they gain +15% attack in the second half.",
    combo4:
      "The opposing Bot Goalkeeper suffers a permanent -15% reflex penalty.",
  },
  staff: {
    key: "staff",
    label: "The Staff",
    synergy: "Safety and defense",
    color: "#3B82F6",
    icon: "📋",
    comboName: "Catenaccio",
    combo2: "The team's overall defense gains +12% power.",
    combo4:
      "The Bot Goalkeeper receives a shield that stops the very first on-target shot of the match.",
  },
  environnement: {
    key: "environnement",
    label: "The Environment",
    synergy: "Chaos and force disruption",
    color: "#5BC8D6",
    icon: "🌪️",
    comboName: "Natural Chaos",
    combo2:
      "The team ignores Environment card penalties — only opponents suffer them.",
    combo4:
      "\"Lightning\": 25% chance to instantly cancel an opponent's goal.",
  },
  vip: {
    key: "vip",
    label: "The VIPs",
    synergy: "Economy and risk / reward",
    color: "#F5B301",
    icon: "💼",
    comboName: "Business Club",
    combo2: "The first daily Reroll in the shop costs 0 Gold.",
    combo4:
      "\"Referee Purchase\": no red cards or penalties against the team this week.",
  },
};

export const FAMILY_LIST: CardFamily[] = [
  "tribune",
  "staff",
  "environnement",
  "vip",
];

/** Famille principale d'une carte (pour le thème couleur). */
export const primaryFamily = (c: CardDef): CardFamily => c.families[0];

/* ─── Les 16 cartes ────────────────────────────────────────────────────────── */
export const CARDS: CardDef[] = [
  // ── Common (tier 1, 1 family) ──
  {
    id: "fanatique",
    name: "The Fanatic",
    families: ["tribune"],
    tier: 1,
    icon: "🔥",
    effect: "+5% overall attack power.",
  },
  {
    id: "tambour",
    name: "The Drummer",
    families: ["tribune"],
    tier: 1,
    icon: "🥁",
    effect:
      "+10% chance of succeeding in the build-up phase (reaching the penalty area).",
  },
  {
    id: "kine",
    name: "The Physio",
    families: ["staff"],
    tier: 1,
    icon: "🩹",
    effect: "-15% chance of a player committing a foul during a tackle.",
  },
  {
    id: "pluie_battante",
    name: "Heavy Rain",
    families: ["environnement"],
    tier: 1,
    icon: "🌧️",
    effect:
      "Slippery pitch: long-range shots have +15% chance of scoring (both teams).",
  },
  {
    id: "pelouse_gelee",
    name: "Frozen Pitch",
    families: ["environnement"],
    tier: 1,
    icon: "❄️",
    effect:
      "Closed match: -20% chance of reaching the opponent's penalty area (both teams).",
  },
  {
    id: "brouillard",
    name: "Thick Fog",
    families: ["environnement"],
    tier: 1,
    icon: "🌫️",
    effect: "Reduced visibility: Bot Goalkeepers on both sides have -10% accuracy.",
  },

  // ── Rare (tier 2, 1 family) ──
  {
    id: "kop_nord",
    name: "North End Kop",
    families: ["tribune"],
    tier: 2,
    icon: "🧣",
    effect: "Intimidates the opponent: reduces the opposing team's defense by 5%.",
  },
  {
    id: "analyste_video",
    name: "Video Analyst",
    families: ["staff"],
    tier: 2,
    icon: "🎥",
    effect: "Reveals tomorrow's opponent's stats and powers.",
  },
  {
    id: "dieteticien",
    name: "The Nutritionist",
    families: ["staff"],
    tier: 2,
    icon: "🥗",
    effect:
      "Peak endurance: players lose no stats in the second half.",
  },
  {
    id: "canicule",
    name: "Heatwave",
    families: ["environnement"],
    tier: 2,
    icon: "🌡️",
    effect: "Extreme fatigue: attack power for both teams drops by 15%.",
  },
  {
    id: "sponsor_qatari",
    name: "Qatari Sponsor",
    families: ["vip"],
    tier: 2,
    icon: "💰",
    effect: "Generates +2 League Golds after each match (win or loss).",
  },

  // ── Epic (tier 3, 2 families) ──
  {
    id: "adjoint_tactique",
    name: "Tactical Assistant",
    families: ["staff", "tribune"],
    tier: 3,
    icon: "📋",
    effect:
      "Automatically shifts a player's position at half-time to counter the opponent.",
  },
  {
    id: "agent_joueur",
    name: "Player Agent",
    families: ["vip", "staff"],
    tier: 3,
    icon: "🕴️",
    effect:
      "Doubles the chance of dropping a Mystery Box at end of week if the team is promoted.",
  },
  {
    id: "celebrite_loge",
    name: "Box Celebrity",
    families: ["vip", "environnement"],
    tier: 3,
    icon: "🌟",
    effect: "If the team wins: Gold earnings doubled. If they lose: 0 Gold.",
  },

  // ── Legendary (tier 4, 3 families) ──
  {
    id: "capo_ultras",
    name: "Ultras Leader",
    families: ["tribune", "staff", "environnement"],
    tier: 4,
    icon: "📣",
    effect:
      "Cancels 1 opponent's goal if it's scored in the last 10 minutes of the match.",
  },
  {
    id: "president_colerique",
    name: "Angry President",
    families: ["vip", "tribune", "staff"],
    tier: 4,
    icon: "🤬",
    effect:
      "+15% stats for all players, BUT the Bot Goalkeeper loses 1 level.",
  },
];

export const CARD_BY_ID: Record<string, CardDef> = CARDS.reduce(
  (acc, c) => ({ ...acc, [c.id]: c }),
  {} as Record<string, CardDef>
);

/* ─── Rareté pondérée ──────────────────────────────────────────────────────── */
export const TIER_WEIGHTS: Record<CardTier, number> = {
  1: 60,
  2: 28,
  3: 9,
  4: 3,
};

export const TIER_META: Record<
  CardTier,
  { label: string; color: string }
> = {
  1: { label: "Common", color: "#9CA7B8" },
  2: { label: "Rare", color: "#3B82F6" },
  3: { label: "Epic", color: "#A855F7" },
  4: { label: "Legendary", color: "#F5B301" },
};

/* ─── Économie ─────────────────────────────────────────────────────────────── */
export const SHOP_SIZE = 3; // 3 cartes proposées par jour et par joueur
export const REROLL_COST = 2; // coût d'un reroll en Golds de ligue
export const STARTER_LEAGUE_GOLD = 12; // dotation de départ
export const CARD_COST: Record<CardTier, number> = { 1: 2, 2: 3, 3: 4, 4: 5 };

/** Récompense de base en Golds de ligue après un match. */
export const MATCH_GOLD_REWARD: Record<"win" | "draw" | "loss", number> = {
  win: 8,
  draw: 5,
  loss: 3,
};

/** Coût en Golds de ligue des orbes au shop (classiques 1, verte 2). */
export const ORB_COST = { ROUGE: 1, BLEUE: 1, VERTE: 2 } as const;

/** Nombre maximum de cartes détenues dans l'inventaire. */
export const MAX_INVENTORY = 3;

/** Remboursement à la revente : 1 Gold de moins que le coût (commune gratuite). */
export const SELL_REFUND: Record<CardTier, number> = { 1: 0, 2: 2, 3: 3, 4: 4 };
